/**
 * @fileoverview Live Stadium Interactive Stage and Zero-Canvas HUD Component for 'Rasca y gana' (Scratcher - Tile ID 43) (/screen).
 * Renders a high-performance 60 FPS GPU-accelerated stadium experience with 0% CPU consumption in idle state.
 * Features a 10x10 DOM CSS Grid scratch mosaic driven by pre-shuffled Fisher-Yates indices,
 * fluid dual/N-player HUD scoreboards with theme typography, countdown overlays,
 * and dramatic victory cinematics (-15° elastic winner stamp, victor celebration avatar, dimmed loser effect, and ephemeral DOM confetti).
 * Excludes any visual timer or seconds clock from the stadium screen (authoritative server-managed timeout).
 * 
 * @module components/Screen/Sections/Scratcher/ScratcherGame
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme, GetRealtimeState } from '../../../../utilities/BL/Scratcher';

/**
 * Total scratch blocks count in the 10x10 CSS Grid.
 * @constant {number}
 */
const TOTAL_SCRATCH_BLOCKS = 100;

/**
 * Array of 100 block cell indices [0..99].
 * @constant {Array<number>}
 */
const BLOCK_INDICES_ARRAY = Array.from({ length: TOTAL_SCRATCH_BLOCKS }, (_, index) => index);

/**
 * Confetti color palette for victory celebration explosion.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00ffcc', '#ff0055', '#ffea00', '#00e676', '#ff1744', '#ffffff', '#e040fb', '#2979ff'];

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_GAME_CONFIGURATION = {
    gameBackgroundType: 'image',
    gameBackgroundColor: '#121224',
    gameBackgroundUrl: '',
    gameTextColor: '#ffffff',
    player1Color: '#00ffcc',
    player2Color: '#ff0055',
    fontFamily: 'Bangers',
    brandLogoUrl: '',
    sponsorLogoUrl: '',
    scratchCoverColor: '#C0C0C0',
    scratchCoverImageUrl: '',
    scratchHiddenImageUrl: '/img/games/briefcase/premio.png',
    targetPercentage: 100
};

/**
 * Default fallback players template.
 * @constant {Array<Object>}
 */
const DEFAULT_PLAYERS = [
    {
        playerId: 'player1',
        slot: 'J1',
        name: 'Jugador 1',
        color: '#00ffcc',
        avatarUrl: '',
        winnerAvatarUrl: '',
        scratchedPercentage: 0,
        totalStrokes: 0,
        isCompleted: false,
        isConnected: false
    },
    {
        playerId: 'player2',
        slot: 'J2',
        name: 'Jugador 2',
        color: '#ff0055',
        avatarUrl: '',
        winnerAvatarUrl: '',
        scratchedPercentage: 0,
        totalStrokes: 0,
        isCompleted: false,
        isConnected: false
    }
];

/**
 * Generates a pre-shuffled permutation of 100 indices using the Fisher-Yates algorithm.
 * 
 * @function generateFisherYatesDeck
 * @returns {Array<number>} Array of 100 elements containing a random permutation of numbers 0..99.
 */
const generateFisherYatesDeck = () => {
    const deck = Array.from({ length: TOTAL_SCRATCH_BLOCKS }, (_, index) => index);
    for (let currentIndex = deck.length - 1; currentIndex > 0; currentIndex--) {
        const randomizedIndex = Math.floor(Math.random() * (currentIndex + 1));
        const temporaryValue = deck[currentIndex];
        deck[currentIndex] = deck[randomizedIndex];
        deck[randomizedIndex] = temporaryValue;
    }
    return deck;
};

/**
 * ScratcherGame Component
 * 
 * @function ScratcherGame
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - Incoming WebSocket packet or snapshot from parent Screen router.
 * @returns {React.ReactElement} The rendered stadium game HUD and stage.
 */
export const ScratcherGame = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN VISUAL
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO (TELEMETRÍA Y ESTADO DE RONDA)
    // =========================================================================
    const [gameState, setGameState] = useState({
        state: 'game_ready', // idle, cover, game_ready, countdown, playing, paused, finished
        gameMode: 'simultaneous',
        targetPercentage: 100,
        maxTimeSeconds: 20,
        winner: null,
        players: DEFAULT_PLAYERS
    });

    // Countdown active count (3, 2, 1, '¡A RASCAR!')
    const [countdownDisplayValue, setCountdownDisplayValue] = useState(null);

    // =========================================================================
    // 3. MAZOS FISHER-YATES PRE-MEZCLADOS POR JUGADOR (ZERO-CANVAS 10x10)
    // =========================================================================
    // Stores a map: { [playerKey]: [array of 100 permutation threshold values] }
    const [playerShuffledDecks, setPlayerShuffledDecks] = useState(() => ({
        player1: generateFisherYatesDeck(),
        player2: generateFisherYatesDeck()
    }));

    // =========================================================================
    // 4. ESTADOS DE ANIMACIÓN GPU Y CONFETI EFÍMERO (4s AUTO-CLEANUP)
    // =========================================================================
    const [ephemeralConfettiList, setEphemeralConfettiList] = useState([]);
    const confettiCleanupTimerRef = useRef(null);
    const countdownDismissTimerRef = useRef(null);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (
            mediaPath.startsWith('http://') ||
            mediaPath.startsWith('https://') ||
            mediaPath.startsWith('blob:') ||
            mediaPath.startsWith('data:')
        ) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
        return apiBaseEndpointUrl ? `${apiBaseEndpointUrl}${sanitizedMediaPath}` : sanitizedMediaPath;
    }, []);

    /**
     * Re-shuffles the Fisher-Yates decks for all active players when starting a new round or match.
     * 
     * @function reshuffleAllPlayerDecks
     * @param {Array<Object>} [playerList] - Optional custom players list.
     */
    const reshuffleAllPlayerDecks = useCallback((playerList = null) => {
        const activePlayers = playerList || gameState.players || DEFAULT_PLAYERS;
        const newDecksMap = {};

        activePlayers.forEach((playerItem) => {
            const playerKey = (playerItem.playerId || playerItem.slot || 'player1').toLowerCase();
            newDecksMap[playerKey] = generateFisherYatesDeck();
        });

        // Always ensure player1 and player2 exist
        if (!newDecksMap.player1) newDecksMap.player1 = generateFisherYatesDeck();
        if (!newDecksMap.player2) newDecksMap.player2 = generateFisherYatesDeck();

        setPlayerShuffledDecks(newDecksMap);
    }, [gameState.players]);

    // =========================================================================
    // 5. CARGA INICIAL DE CONFIGURACIÓN Y ESTADO DE SALA
    // =========================================================================
    const fetchInitialGameConfiguration = useCallback(async () => {
        try {
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                const roomState = realtimeResponse.data;
                if (roomState.activeTheme) {
                    setActiveThemeData(roomState.activeTheme);
                } else if (roomState.activeThemeId) {
                    const themeResponse = await GetTheme(roomState.activeThemeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }

                setGameState((prevState) => ({
                    ...prevState,
                    ...roomState,
                    players: (roomState.players && roomState.players.length > 0) ? roomState.players : prevState.players
                }));

                reshuffleAllPlayerDecks(roomState.players);
            } else {
                const configResponse = await GetConfig();
                if (configResponse.success && configResponse.data) {
                    if (configResponse.data.activeTheme) {
                        setActiveThemeData(configResponse.data.activeTheme);
                    }
                }
            }
        } catch (initialFetchError) {
            // Non-blocking fallback
        }
    }, [reshuffleAllPlayerDecks]);

    useEffect(() => {
        if (!activeThemeData) {
            fetchInitialGameConfiguration();
        }
    }, [activeThemeData, fetchInitialGameConfiguration]);

    // =========================================================================
    // 6. DISPARADOR DE CONFETI EFÍMERO (4 SEGUNDOS EXACTOS - AUTO-DESTRUCCIÓN)
    // =========================================================================
    const triggerEphemeralConfettiBurst = useCallback(() => {
        const TOTAL_CONFETTI_COUNT = 75;
        const generatedConfetti = Array.from({ length: TOTAL_CONFETTI_COUNT }, (_, particleIndex) => {
            const randomizedColor = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];
            const randomizedHorizontalPosition = Math.random() * 100;
            const randomizedFallDuration = 2.0 + Math.random() * 1.5;
            const randomizedDelay = Math.random() * 0.5;
            const randomizedSize = 14 + Math.random() * 18;
            const isCircular = Math.random() > 0.45;

            return {
                id: `scratcher-confetti-${particleIndex}-${Date.now()}`,
                color: randomizedColor,
                leftPercentage: randomizedHorizontalPosition,
                durationSeconds: randomizedFallDuration,
                delaySeconds: randomizedDelay,
                sizePixels: randomizedSize,
                borderRadius: isCircular ? '50%' : '2px'
            };
        });

        setEphemeralConfettiList(generatedConfetti);

        if (confettiCleanupTimerRef.current) {
            clearTimeout(confettiCleanupTimerRef.current);
        }

        confettiCleanupTimerRef.current = setTimeout(() => {
            setEphemeralConfettiList([]);
        }, 4000);
    }, []);

    // =========================================================================
    // 7. PROCESAMIENTO DE MENSAJES Y TELEMETRÍA WEBSOCKET
    // =========================================================================
    useEffect(() => {
        if (!payload || !payload.type) return;

        const normalizedType = payload.type.toLowerCase();
        const incomingState = payload.state || payload.data || payload.payload || {};

        if (incomingState.activeTheme) {
            setActiveThemeData(incomingState.activeTheme);
        } else if (payload.theme) {
            setActiveThemeData(payload.theme);
        }

        switch (normalizedType) {
            case 'scratcher.prepare':
            case 'scratcher_show_game': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'game_ready',
                    winner: null
                }));
                setCountdownDisplayValue(null);
                setEphemeralConfettiList([]);
                reshuffleAllPlayerDecks(incomingState.players);
                break;
            }

            case 'scratcher_countdown': {
                const countNumber = payload.count !== undefined ? payload.count : incomingState.count;
                setCountdownDisplayValue(countNumber);
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'countdown',
                    winner: null
                }));

                if (countNumber === 1 || countNumber === 0) {
                    if (countdownDismissTimerRef.current) clearTimeout(countdownDismissTimerRef.current);
                    countdownDismissTimerRef.current = setTimeout(() => {
                        setCountdownDisplayValue(null);
                    }, 1200);
                }
                break;
            }

            case 'scratcher_game_started':
            case 'scratcher_round_started': {
                setCountdownDisplayValue('¡A RASCAR!');
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'playing',
                    winner: null
                }));

                if (countdownDismissTimerRef.current) clearTimeout(countdownDismissTimerRef.current);
                countdownDismissTimerRef.current = setTimeout(() => {
                    setCountdownDisplayValue(null);
                }, 1000);

                reshuffleAllPlayerDecks(incomingState.players);
                break;
            }

            case 'scratcher_update_names':
            case 'scratcher_set_player_names':
            case 'scratcher_players_updated':
            case 'scratcher_progress_sync':
            case 'scratcher_telemetry': {
                // Update players list progress & names
                const incomingPlayers =
                    payload.data?.players ||
                    incomingState.players ||
                    payload.players ||
                    [];

                if (Array.isArray(incomingPlayers) && incomingPlayers.length > 0) {
                    setGameState((prev) => ({
                        ...prev,
                        players: incomingPlayers
                    }));
                }

                if (payload.player1Name || payload.player2Name) {
                    setActiveThemeData((prev) => ({
                        ...prev,
                        player1Name: payload.player1Name || prev?.player1Name,
                        player2Name: payload.player2Name || prev?.player2Name
                    }));
                } else if (incomingState.activeTheme) {
                    setActiveThemeData(incomingState.activeTheme);
                }
                break;
            }

            case 'scratcher_game_over':
            case 'scratcher_game_finished': {
                const winnerPlayerId = payload.winner || incomingState.winner;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'finished',
                    winner: winnerPlayerId
                }));
                setCountdownDisplayValue(null);
                triggerEphemeralConfettiBurst();
                break;
            }

            case 'scratcher_paused': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'paused'
                }));
                break;
            }

            case 'scratcher_resumed': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'playing'
                }));
                break;
            }

            case 'scratcher_reset':
            case 'scratcher_match_reset': {
                const incomingPlayers =
                    incomingState.players ||
                    payload.players ||
                    payload.data?.players ||
                    [];

                setGameState((prev) => {
                    // Merge previous player names if incoming has empty names
                    const mergedPlayers = (Array.isArray(incomingPlayers) && incomingPlayers.length > 0)
                        ? incomingPlayers.map((p) => {
                              const prevMatch = prev.players?.find((oldP) => oldP.playerId === p.playerId || oldP.slot === p.slot);
                              return {
                                  ...p,
                                  name: p.name?.trim() || prevMatch?.name?.trim() || ''
                              };
                          })
                        : prev.players;

                    return {
                        ...prev,
                        ...incomingState,
                        state: 'idle',
                        winner: null,
                        players: mergedPlayers
                    };
                });

                if (incomingState.activeTheme) {
                    setActiveThemeData((prev) => ({
                        ...prev,
                        ...incomingState.activeTheme,
                        player1Name: incomingState.activeTheme?.player1Name || prev?.player1Name,
                        player2Name: incomingState.activeTheme?.player2Name || prev?.player2Name
                    }));
                }

                setCountdownDisplayValue(null);
                setEphemeralConfettiList([]);
                reshuffleAllPlayerDecks();
                break;
            }

            case 'scratcher_snapshot':
            case 'scratcher_state': {
                if (incomingState && typeof incomingState === 'object') {
                    setGameState((prev) => ({
                        ...prev,
                        ...incomingState
                    }));
                }
                break;
            }

            default:
                break;
        }
    }, [payload, reshuffleAllPlayerDecks, triggerEphemeralConfettiBurst]);

    // Timer cleanup on unmount
    useEffect(() => {
        return () => {
            if (confettiCleanupTimerRef.current) clearTimeout(confettiCleanupTimerRef.current);
            if (countdownDismissTimerRef.current) clearTimeout(countdownDismissTimerRef.current);
        };
    }, []);

    // =========================================================================
    // 8. EXTRACCIÓN DE ATRIBUTOS DEL TEMA Y JUGADORES
    // =========================================================================
    const gameBackgroundType = activeThemeData?.gameBackgroundType || DEFAULT_GAME_CONFIGURATION.gameBackgroundType;
    const gameBackgroundColor = activeThemeData?.gameBackgroundColor || DEFAULT_GAME_CONFIGURATION.gameBackgroundColor;
    const gameBackgroundUrl = activeThemeData?.gameBackgroundUrl ? resolveMediaUrl(activeThemeData.gameBackgroundUrl) : '';
    const gameTextColor = activeThemeData?.gameTextColor || DEFAULT_GAME_CONFIGURATION.gameTextColor;
    const brandLogoUrl = activeThemeData?.brandLogoUrl ? resolveMediaUrl(activeThemeData.brandLogoUrl) : '';
    const themeFontFamily = activeThemeData?.fontFamily || DEFAULT_GAME_CONFIGURATION.fontFamily;

    const scratchCoverColor = activeThemeData?.scratchCoverColor || DEFAULT_GAME_CONFIGURATION.scratchCoverColor;
    const scratchCoverImageUrl = activeThemeData?.scratchCoverImageUrl ? resolveMediaUrl(activeThemeData.scratchCoverImageUrl) : '';
    const scratchHiddenImageUrl = activeThemeData?.scratchHiddenImageUrl || activeThemeData?.scratchHiddenPrizeUrl
        ? resolveMediaUrl(activeThemeData?.scratchHiddenImageUrl || activeThemeData?.scratchHiddenPrizeUrl)
        : DEFAULT_GAME_CONFIGURATION.scratchHiddenImageUrl;

    // Normalización de Jugador 1 y Jugador 2
    const player1Telemetry = gameState.players?.find((p) => (p.playerId && p.playerId.toLowerCase() === 'player1') || (p.slot && p.slot.toUpperCase() === 'J1')) || gameState.players?.[0] || DEFAULT_PLAYERS[0];
    const player2Telemetry = gameState.players?.find((p) => (p.playerId && p.playerId.toLowerCase() === 'player2') || (p.slot && p.slot.toUpperCase() === 'J2')) || gameState.players?.[1] || DEFAULT_PLAYERS[1];

    const player1Name = player1Telemetry?.name?.trim() || activeThemeData?.player1Name?.trim() || 'Jugador 1';
    const player2Name = player2Telemetry?.name?.trim() || activeThemeData?.player2Name?.trim() || 'Jugador 2';

    const player1Color = activeThemeData?.player1Color || player1Telemetry.color || DEFAULT_GAME_CONFIGURATION.player1Color;
    const player2Color = activeThemeData?.player2Color || player2Telemetry.color || DEFAULT_GAME_CONFIGURATION.player2Color;

    const player1Progress = Math.min(100, Math.max(0, Math.round(player1Telemetry.scratchedPercentage || 0)));
    const player2Progress = Math.min(100, Math.max(0, Math.round(player2Telemetry.scratchedPercentage || 0)));

    // Winner detection
    const isGameOver = gameState.state === 'finished';
    const winnerIdNormalized = (gameState.winner || '').toLowerCase();

    const isPlayer1Winner = isGameOver && (
        winnerIdNormalized === 'player1' ||
        winnerIdNormalized === 'j1' ||
        winnerIdNormalized === player1Name.toLowerCase() ||
        (player1Progress >= 100 && player2Progress < 100)
    );

    const isPlayer2Winner = isGameOver && (
        winnerIdNormalized === 'player2' ||
        winnerIdNormalized === 'j2' ||
        winnerIdNormalized === player2Name.toLowerCase() ||
        (player2Progress >= 100 && player1Progress < 100)
    );

    const isTieMatch = isGameOver && !isPlayer1Winner && !isPlayer2Winner && (
        winnerIdNormalized === 'empate' ||
        winnerIdNormalized === 'tie' ||
        Math.abs(player1Progress - player2Progress) === 0
    );

    // Dynamic Winner vs Normal Avatars
    const player1AvatarToDisplay = isPlayer1Winner
        ? (activeThemeData?.player1Winner ? resolveMediaUrl(activeThemeData.player1Winner) : resolveMediaUrl(activeThemeData?.player1Avatar || player1Telemetry.avatarUrl))
        : resolveMediaUrl(activeThemeData?.player1Avatar || player1Telemetry.avatarUrl);

    const player2AvatarToDisplay = isPlayer2Winner
        ? (activeThemeData?.player2Winner ? resolveMediaUrl(activeThemeData.player2Winner) : resolveMediaUrl(activeThemeData?.player2Avatar || player2Telemetry.avatarUrl))
        : resolveMediaUrl(activeThemeData?.player2Avatar || player2Telemetry.avatarUrl);

    // Extra Players (if N > 2)
    const extraPlayersList = useMemo(() => {
        if (!gameState.players || gameState.players.length <= 2) return [];
        return gameState.players.filter((p, index) => {
            const isP1 = (p.playerId && p.playerId.toLowerCase() === 'player1') || (p.slot && p.slot.toUpperCase() === 'J1') || index === 0;
            const isP2 = (p.playerId && p.playerId.toLowerCase() === 'player2') || (p.slot && p.slot.toUpperCase() === 'J2') || index === 1;
            return !isP1 && !isP2;
        });
    }, [gameState.players]);

    // =========================================================================
    // 9. RENDERIZADO DEL FONDO DINÁMICO
    // =========================================================================
    const renderGameBackgroundLayer = () => {
        if (gameBackgroundType === 'video' && gameBackgroundUrl) {
            return (
                <video
                    src={gameBackgroundUrl}
                    className="scratcher-screen-game__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (gameBackgroundType === 'image' && gameBackgroundUrl) {
            return (
                <div
                    className="scratcher-screen-game__background-image"
                    style={{ backgroundImage: `url(${gameBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="scratcher-screen-game__background-color"
                style={{ backgroundColor: gameBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 10. RENDERIZADO DEL MOSAICO DE RASPADO 10x10 ZERO-CANVAS
    // =========================================================================
    /**
     * Renders a 10x10 CSS Grid scratch arena for a specific player using their Fisher-Yates shuffled deck.
     * 
     * @function renderScratchArenaCard
     * @param {string} playerKey - 'player1', 'player2', etc.
     * @param {string} playerName - Player display name.
     * @param {number} progressPercentage - 0 to 100.
     * @param {boolean} isWinner - Whether this player won.
     * @param {boolean} isLoser - Whether this player lost.
     * @param {string} playerColor - Accent color.
     */
    const renderScratchArenaCard = (
        playerKey,
        playerName,
        progressPercentage,
        isWinner,
        isLoser,
        playerColor
    ) => {
        const normalizedKey = playerKey.toLowerCase();
        const playerDeck = playerShuffledDecks[normalizedKey] || playerShuffledDecks.player1 || BLOCK_INDICES_ARRAY;

        // Cover block style: either texture image or solid metallic color
        const blockCoverStyle = scratchCoverImageUrl
            ? {
                  backgroundImage: `url(${scratchCoverImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
              }
            : {
                  backgroundColor: scratchCoverColor
              };

        return (
            <div
                className={`scratcher-screen-game__scratch-card ${
                    isWinner ? 'scratcher-screen-game__scratch-card--winner' : ''
                } ${isLoser ? 'scratcher-screen-game__scratch-card--defeated' : ''}`}
                style={{ borderColor: playerColor }}
            >
                {/* Capa Inferior: Imagen del Premio en Alta Resolución */}
                <div
                    className="scratcher-screen-game__hidden-prize-layer"
                    style={{
                        backgroundImage: `url(${scratchHiddenImageUrl})`
                    }}
                >
                    {!scratchHiddenImageUrl && (
                        <div className="scratcher-screen-game__prize-fallback-text">
                            ¡PREMIO OFICIAL ADSL!
                        </div>
                    )}
                </div>

                {/* Capa Superior: Mosaico 10x10 Zero-Canvas (100 div.scratch-block) */}
                <div className="scratcher-screen-game__scratch-mosaic-grid">
                    {BLOCK_INDICES_ARRAY.map((blockIndex) => {
                        // Fisher-Yates threshold: blockIndex is revealed if its pre-assigned threshold < progressPercentage
                        const revealThreshold = playerDeck[blockIndex];
                        const isRevealed = revealThreshold < progressPercentage;

                        return (
                            <div
                                key={`block-${playerKey}-${blockIndex}`}
                                className={`scratcher-screen-game__scratch-block ${
                                    isRevealed ? 'scratcher-screen-game__scratch-block--revealed' : ''
                                }`}
                                style={blockCoverStyle}
                            />
                        );
                    })}
                </div>

                {/* Sello de Victoria Rotado -15° con Rebote Elástico */}
                {isWinner && (
                    <div className="scratcher-screen-game__winner-stamp">
                        <span className="scratcher-screen-game__winner-stamp-text">
                            ¡GANADOR!
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // =========================================================================
    // 11. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <main
            className="scratcher-screen-game"
            role="region"
            aria-label="Escenario de Combate Rasca y Gana"
            style={{ fontFamily: themeFontFamily }}
        >
            {/* Fondo Dinámico GPU Composited */}
            {renderGameBackgroundLayer()}

            {/* Viñeta de Contraste */}
            <div className="scratcher-screen-game__vignette-overlay" />

            {/* Lluvia de Confeti Efímero (Auto-limpieza a los 4s) */}
            {ephemeralConfettiList.length > 0 && (
                <div className="scratcher-screen-game__confetti-container">
                    {ephemeralConfettiList.map((confettiParticle) => (
                        <div
                            key={confettiParticle.id}
                            className="scratcher-screen-game__confetti-particle"
                            style={{
                                backgroundColor: confettiParticle.color,
                                left: `${confettiParticle.leftPercentage}%`,
                                width: `${confettiParticle.sizePixels}px`,
                                height: `${confettiParticle.sizePixels * 1.5}px`,
                                borderRadius: confettiParticle.borderRadius,
                                animationDuration: `${confettiParticle.durationSeconds}s`,
                                animationDelay: `${confettiParticle.delaySeconds}s`
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Overlay de Cuenta Regresiva (3, 2, 1, ¡A RASCAR!) */}
            {countdownDisplayValue !== null && (
                <div className="scratcher-screen-game__countdown-overlay">
                    <div className="scratcher-screen-game__countdown-badge">
                        {countdownDisplayValue}
                    </div>
                </div>
            )}

            {/* ============================================================= */}
            {/* HUD SUPERIOR: JUGADOR 1 VS JUGADOR 2 Y LOGO CENTRAL           */}
            {/* ============================================================= */}
            <header className="scratcher-screen-game__hud-header">
                {/* JUGADOR 1 HUD CARD */}
                <div
                    className={`scratcher-screen-game__hud-player scratcher-screen-game__hud-player--p1 ${
                        isPlayer1Winner ? 'scratcher-screen-game__hud-player--winner' : ''
                    } ${isGameOver && !isPlayer1Winner && !isTieMatch ? 'scratcher-screen-game__hud-player--defeated' : ''}`}
                    style={{ borderLeftColor: player1Color }}
                >
                    <div
                        className="scratcher-screen-game__player-avatar-box"
                        style={{ borderColor: player1Color }}
                    >
                        {player1AvatarToDisplay ? (
                            <img
                                src={player1AvatarToDisplay}
                                alt={player1Name}
                                className="scratcher-screen-game__player-avatar"
                            />
                        ) : (
                            <span className="scratcher-screen-game__player-avatar-fallback">
                                {player1Name.charAt(0) || '1'}
                            </span>
                        )}
                    </div>

                    <div className="scratcher-screen-game__player-details">
                        <div className="scratcher-screen-game__player-name-row">
                            <span
                                className="scratcher-screen-game__player-name"
                                style={{ color: gameTextColor }}
                            >
                                {player1Name}
                            </span>
                            <span className="scratcher-screen-game__player-slot-badge" style={{ backgroundColor: player1Color }}>
                                J1
                            </span>
                        </div>

                        {/* Barra de Progreso Fluida */}
                        <div className="scratcher-screen-game__progress-bar-track">
                            <div
                                className="scratcher-screen-game__progress-bar-fill"
                                style={{
                                    width: `${player1Progress}%`,
                                    backgroundColor: player1Color,
                                    boxShadow: `0 0 12px ${player1Color}`
                                }}
                            />
                        </div>
                    </div>

                    {/* Porcentaje Numérico Gigante */}
                    <div className="scratcher-screen-game__percentage-box">
                        <span
                            className="scratcher-screen-game__percentage-value"
                            style={{ color: player1Color }}
                        >
                            {player1Progress}%
                        </span>
                    </div>
                </div>

                {/* CENTRO DEL HUD: LOGOTIPO Y ESTADO DE BATALLA */}
                <div className="scratcher-screen-game__hud-center">
                    <img
                        src={brandLogoUrl || '/img/page/adsl-five.png'}
                        alt="ADSL"
                        className="scratcher-screen-game__brand-logo-hud"
                        style={{ maxWidth: '450px', maxHeight: '85px' }}
                    />

                    <span className="scratcher-screen-game__status-badge">
                        {isGameOver
                            ? (isTieMatch ? '¡EMPATE ÉPICO!' : `¡VICTORIA DE ${isPlayer1Winner ? player1Name : player2Name}!`)
                            : gameState.state === 'countdown'
                            ? '¡PREPÁRENSE!'
                            : gameState.state === 'playing'
                            ? '⚡ ¡A RASPAR!'
                            : gameState.state === 'paused'
                            ? '⏸ PARTIDA PAUSADA'
                            : '🎮 DUELO DE RASPADO'}
                    </span>
                </div>

                {/* JUGADOR 2 HUD CARD */}
                <div
                    className={`scratcher-screen-game__hud-player scratcher-screen-game__hud-player--p2 ${
                        isPlayer2Winner ? 'scratcher-screen-game__hud-player--winner' : ''
                    } ${isGameOver && !isPlayer2Winner && !isTieMatch ? 'scratcher-screen-game__hud-player--defeated' : ''}`}
                    style={{ borderRightColor: player2Color }}
                >
                    {/* Porcentaje Numérico Gigante */}
                    <div className="scratcher-screen-game__percentage-box">
                        <span
                            className="scratcher-screen-game__percentage-value"
                            style={{ color: player2Color }}
                        >
                            {player2Progress}%
                        </span>
                    </div>

                    <div className="scratcher-screen-game__player-details scratcher-screen-game__player-details--p2">
                        <div className="scratcher-screen-game__player-name-row">
                            <span className="scratcher-screen-game__player-slot-badge" style={{ backgroundColor: player2Color }}>
                                J2
                            </span>
                            <span
                                className="scratcher-screen-game__player-name"
                                style={{ color: gameTextColor }}
                            >
                                {player2Name}
                            </span>
                        </div>

                        {/* Barra de Progreso Fluida */}
                        <div className="scratcher-screen-game__progress-bar-track">
                            <div
                                className="scratcher-screen-game__progress-bar-fill"
                                style={{
                                    width: `${player2Progress}%`,
                                    backgroundColor: player2Color,
                                    boxShadow: `0 0 12px ${player2Color}`
                                }}
                            />
                        </div>
                    </div>

                    <div
                        className="scratcher-screen-game__player-avatar-box"
                        style={{ borderColor: player2Color }}
                    >
                        {player2AvatarToDisplay ? (
                            <img
                                src={player2AvatarToDisplay}
                                alt={player2Name}
                                className="scratcher-screen-game__player-avatar"
                            />
                        ) : (
                            <span className="scratcher-screen-game__player-avatar-fallback">
                                {player2Name.charAt(0) || '2'}
                            </span>
                        )}
                    </div>
                </div>
            </header>

            {/* ============================================================= */}
            {/* ESCENARIO DE COMBATE: MOSAICOS DE RASPADO 10x10 ZERO-CANVAS   */}
            {/* ============================================================= */}
            <section className="scratcher-screen-game__battle-stage">
                {/* TARJETA DE RASPADO JUGADOR 1 */}
                <div className="scratcher-screen-game__player-column">
                    <div className="scratcher-screen-game__player-label-badge" style={{ color: player1Color }}>
                        {player1Name}
                    </div>
                    {renderScratchArenaCard(
                        'player1',
                        player1Name,
                        player1Progress,
                        isPlayer1Winner,
                        isGameOver && !isPlayer1Winner && !isTieMatch,
                        player1Color
                    )}
                </div>

                {/* ELEMENTO CENTRAL: VS INSIGNIA */}
                <div className="scratcher-screen-game__vs-divider">
                    <span className="scratcher-screen-game__vs-text">VS</span>
                </div>

                {/* TARJETA DE RASPADO JUGADOR 2 */}
                <div className="scratcher-screen-game__player-column">
                    <div className="scratcher-screen-game__player-label-badge" style={{ color: player2Color }}>
                        {player2Name}
                    </div>
                    {renderScratchArenaCard(
                        'player2',
                        player2Name,
                        player2Progress,
                        isPlayer2Winner,
                        isGameOver && !isPlayer2Winner && !isTieMatch,
                        player2Color
                    )}
                </div>

                {/* SOPORTE ELÁSTICO PARA JUGADORES ADICIONALES (N > 2) */}
                {extraPlayersList.map((extraPlayerItem, extraIndex) => {
                    const extraKey = (extraPlayerItem.playerId || extraPlayerItem.slot || `extra_${extraIndex}`).toLowerCase();
                    const extraProgress = Math.min(100, Math.max(0, Math.round(extraPlayerItem.scratchedPercentage || 0)));
                    const isExtraWinner = isGameOver && winnerIdNormalized === extraKey;
                    const extraColor = extraPlayerItem.color || '#ffbb00';

                    return (
                        <div key={extraKey} className="scratcher-screen-game__player-column">
                            <div className="scratcher-screen-game__player-label-badge" style={{ color: extraColor }}>
                                {extraPlayerItem.name || `Jugador ${extraIndex + 3}`}
                            </div>
                            {renderScratchArenaCard(
                                extraKey,
                                extraPlayerItem.name,
                                extraProgress,
                                isExtraWinner,
                                isGameOver && !isExtraWinner && !isTieMatch,
                                extraColor
                            )}
                        </div>
                    );
                })}
            </section>

            {/* Pie de Página Corporativo con Créditos */}
            <footer
                className="scratcher-screen-cover__footer-credits"
                style={{ color: '#fff', fontSize: '1.6em' }}
            >
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

ScratcherGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object,
        winner: PropTypes.string,
        count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        players: PropTypes.array
    })
};

ScratcherGame.defaultProps = {
    payload: null
};
